/**
 * Tiny fetch wrapper.
 *
 * - Always sends credentials (cookies)
 * - Throws ApiError on non-2xx with parsed { error, detail }
 * - JSON response auto-parsed
 */

export interface ApiErrorShape {
  ok: false;
  error: string;
  detail?: string;
}

export class ApiError extends Error {
  status: number;
  body: ApiErrorShape | null;
  constructor(status: number, body: ApiErrorShape | null) {
    super(body?.detail || body?.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  return handle<T>(res);
}

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    throw new ApiError(res.status, (json as ApiErrorShape) ?? null);
  }
  return json as T;
}

/**
 * Stream a POST request as Server-Sent Events.
 * Calls onEvent with each parsed { event, data } pair, resolves on the `done` event
 * (or when the connection closes). Throws on transport failure.
 */
export async function apiStreamPost(
  path: string,
  body: unknown,
  onEvent: (ev: { event: string; data: string }) => void,
): Promise<void> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, null);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by a blank line (\n\n).
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    for (const block of blocks) {
      let eventName = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (data) onEvent({ event: eventName, data });
      if (eventName === 'done') return;
    }
  }
}
